/** Amounts are major units. Never combine unlike currencies without an FX rate. */
export function formatMoney(amount: number, currency = 'INR'): string {
  currency = currency.trim().toUpperCase() || 'INR';
  if (!/^[A-Z]{3}$/.test(currency)) return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(amount)} ${currency}`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2
  }).format(amount);
}

export function formatINR(amount: number): string {
  return formatMoney(amount, 'INR');
}

export function formatCurrencyTotals(rows: readonly { amount: number; currency: string }[]): string {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const currency = row.currency?.trim().toUpperCase() || 'INR';
    totals.set(currency, (totals.get(currency) ?? 0) + row.amount);
  }
  return [...totals].sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) => formatMoney(amount, currency)).join(' · ') || formatINR(0);
}

/** Compact display for dashboards: ₹94.6 L, ₹2.84 Cr, ₹38,000. Never sums currencies. */
export function formatCompactMoney(amount: number, currency = 'INR'): string {
  currency = currency.trim().toUpperCase() || 'INR';
  if (currency !== 'INR' || !Number.isFinite(amount)) {
    if (!/^[A-Z]{3}$/.test(currency)) return formatMoney(amount, currency);
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(amount);
  }
  const abs = Math.abs(amount), sign = amount < 0 ? '-' : '';
  const trim = (value: number, digits: number) => value.toFixed(digits).replace(/\.?0+$/, '');
  if (abs >= 1e7) return `${sign}₹${trim(abs / 1e7, 2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${trim(abs / 1e5, 1)} L`;
  return `${sign}₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(abs)}`;
}
export function formatCompactTotals(rows: readonly { amount: number; currency: string }[]): string {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const currency = row.currency?.trim().toUpperCase() || 'INR';
    totals.set(currency, (totals.get(currency) ?? 0) + row.amount);
  }
  return [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([currency, amount]) => formatCompactMoney(amount, currency)).join(' · ') || formatCompactMoney(0);
}
